import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FiGrid, FiActivity, FiHelpCircle, FiInfo, FiCalendar,
  FiMapPin, FiInbox, FiUsers, FiAward, FiLogOut, FiHome, FiMessageSquare,
  FiChevronDown, FiLayers, FiMenu, FiX,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import {
  sportTypeAPI, eventAPI, trainerAPI, gymAPI, groupAPI,
  studentAPI, enrolmentAPI, achievementAPI, faqAPI, aboutUsAPI,
  requestAPI, commentAPI, trainingSchoolAPI,
  lessonAPI, attendanceAPI, transactionAPI
} from '../../api/services';

/* Map each route path to its API for fetching counts */
const COUNT_APIS = {
  '/admin/sport-types': sportTypeAPI,
  '/admin/events': eventAPI,
  '/admin/trainers': trainerAPI,
  '/admin/branches': gymAPI,
  '/admin/training-schools': trainingSchoolAPI,
  '/admin/groups': groupAPI,
  '/admin/students': studentAPI,
  '/admin/enrolments': enrolmentAPI,
  '/admin/lessons': lessonAPI,
  '/admin/attendances': attendanceAPI,
  '/admin/achievements': achievementAPI,
  '/admin/faqs': faqAPI,
  '/admin/about-us': aboutUsAPI,
  '/admin/requests': requestAPI,
  '/admin/comments': commentAPI,
  '/admin/transactions': transactionAPI,
};



const CountBadge = ({ count }) => {
  if (count === null || count === undefined) return null;
  return (
    <span style={badgeStyle}>
      {count}
    </span>
  );
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [counts, setCounts] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { t } = useTranslation();

  const MENU_ITEMS = [
    { path: '/admin', icon: <FiGrid />, label: t('admin.dashboard'), end: true },
    { path: '/admin/sport-types', icon: <FiActivity />, label: t('admin.sport_types') },
    { path: '/admin/events', icon: <FiCalendar />, label: t('admin.events') },
    { path: '/admin/trainers', icon: <FiUsers />, label: t('admin.trainers') },
    { path: '/admin/training-schools', icon: <FiGrid />, label: t('admin.training_schools') },
    { path: '/admin/branches', icon: <FiMapPin />, label: t('admin.branches') },
    { path: '/admin/transactions', icon: <FiActivity />, label: t('admin.transactions') },
    { 
      label: t('admin.groups'), 
      icon: <FiLayers />, 
      children: [
        { path: '/admin/groups', label: t('admin.manage_groups') },
        { path: '/admin/students', label: t('admin.manage_students') },
        { path: '/admin/enrolments', label: t('admin.manage_enrolments') },
        { path: '/admin/lessons', label: t('admin.manage_lessons') },
        { path: '/admin/attendances', label: t('admin.attendance_records') },
      ]
    },
    { path: '/admin/achievements', icon: <FiAward />, label: t('admin.achievements') },
    { path: '/admin/faqs', icon: <FiHelpCircle />, label: t('admin.faqs') },
    { path: '/admin/about-us', icon: <FiInfo />, label: t('admin.about_us') },
    { path: '/admin/requests', icon: <FiInbox />, label: t('admin.requests') },
    { path: '/admin/comments', icon: <FiMessageSquare />, label: t('admin.comments') },
  ];

  useEffect(() => {
    // Fetch counts for all entities
    Object.entries(COUNT_APIS).forEach(([path, api]) => {
      api.getAll()
        .then(res => {
          const data = res.data?.data;
          const count = Array.isArray(data) ? data.length : 0;
          setCounts(prev => ({ ...prev, [path]: count }));
        })
        .catch(() => {
          // Silently ignore — badge just won't show
        });
    });
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSubmenu = (label) => {
    setOpenSubmenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  /* Sum counts for parent items with children */
  const getParentCount = (children) => {
    let total = 0;
    let hasAny = false;
    children.forEach(child => {
      if (counts[child.path] !== undefined) {
        total += counts[child.path];
        hasAny = true;
      }
    });
    return hasAny ? total : null;
  };

  return (
    <div className="admin-layout-container">
      {/* Mobile Overlay */}
      <div 
        className={`admin-overlay ${isSidebarOpen ? 'show' : ''}`} 
        onClick={() => setIsSidebarOpen(false)}
      ></div>

      {/* Sidebar */}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div style={{ padding: '20px 18px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <NavLink to="/" className="navbar-logo" style={{ fontSize: '0.95rem' }}>
            <img src="/logo.png" alt="UzSport" style={{ height: 32, width: 'auto' }} />
            <span>UzSport</span>
          </NavLink>
          <button 
            className="admin-menu-toggle" 
            onClick={() => setIsSidebarOpen(false)}
            style={{ display: isSidebarOpen ? 'block' : 'none' }}
          >
            <FiX />
          </button>
        </div>

        <nav style={{ flex: 1, padding: '0 10px', overflowY: 'auto' }}>
          {MENU_ITEMS.map((item) => {
            if (item.children) {
              const isOpen = openSubmenus[item.label];
              const parentCount = getParentCount(item.children);
              return (
                <div key={item.label}>
                  <button
                    onClick={() => toggleSubmenu(item.label)}
                    style={{ ...menuItemStyle, width: '100%', justifyContent: 'space-between' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      {item.icon}
                      <span>{item.label}</span>
                      <CountBadge count={parentCount} />
                    </div>
                    <FiChevronDown style={{ 
                      transition: 'transform 0.2s', 
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' 
                    }} />
                  </button>
                  {isOpen && (
                    <div style={{ paddingLeft: 20 }}>
                      {item.children.map(child => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          onClick={() => setIsSidebarOpen(false)}
                          style={({ isActive }) => ({
                            ...menuItemStyle,
                            background: isActive ? 'var(--accent-bg)' : 'transparent',
                            color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
                            fontWeight: isActive ? 600 : 500,
                            fontSize: '0.8rem',
                            justifyContent: 'space-between',
                          })}
                        >
                          <span>{child.label}</span>
                          <CountBadge count={counts[child.path]} />
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={() => setIsSidebarOpen(false)}
                style={({ isActive }) => ({
                  ...menuItemStyle,
                  background: isActive ? 'var(--accent-bg)' : 'transparent',
                  color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
                  fontWeight: isActive ? 600 : 500,
                  justifyContent: 'space-between',
                })}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {counts[item.path] !== undefined && <CountBadge count={counts[item.path]} />}
              </NavLink>
            );
          })}
        </nav>

        <div style={{ padding: '14px 10px', borderTop: '1px solid var(--border)' }}>
          <NavLink to="/" style={{ ...menuItemStyle, color: 'var(--text-muted)', marginBottom: 2 }}>
            <FiHome /> <span>{t('nav.back')}</span>
          </NavLink>
          <button onClick={handleLogout} style={{ ...menuItemStyle, width: '100%', color: 'var(--red)', cursor: 'pointer' }}>
            <FiLogOut /> <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <button 
            className="admin-menu-toggle" 
            onClick={() => setIsSidebarOpen(true)}
          >
            <FiMenu />
          </button>
          <div>
            <h2 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              {t('admin.welcome')},
            </h2>
            <h1 style={{ fontSize: '1.05rem', fontWeight: 700 }}>
              {user?.fullName || 'Admin'}
            </h1>
          </div>
        </header>

        <div style={{ padding: '22px 28px' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 6px',
  borderRadius: 10,
  fontSize: '0.7rem',
  fontWeight: 700,
  background: 'var(--accent)',
  color: '#fff',
  lineHeight: 1,
};

const menuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '9px 13px',
  borderRadius: 10,
  fontSize: '0.85rem',
  fontWeight: 500,
  transition: 'all 0.2s',
  textDecoration: 'none',
  marginBottom: 2,
  background: 'transparent',
  border: 'none',
  textAlign: 'left',
};

export default AdminLayout;
